<template>
  <div v-loading="loading" class="waybill-map-page" :class="{ 'waybill-map-page--share': shareMode, 'waybill-map-page--embed': embedMode }">
    <section v-if="shareMode && shareError" data-test="share-error-page" class="waybill-share-error" role="alert">
      <div class="waybill-share-error__card">
        <i class="el-icon-warning-outline waybill-share-error__icon" aria-hidden="true" />
        <h1>分享链接不可用</h1>
        <p>该分享链接无效、已撤销或已过期，请联系分享人重新获取。</p>
      </div>
    </section>
    <header v-if="loaded && !shareMode && !embedMode" class="waybill-map-page__actions">
      <div><strong>{{ waybill.vehicleNo || waybill.vno || '运单轨迹' }}</strong><span>{{ waybill.waybillNo || waybill.transportNo }}</span></div>
      <div>
        <el-button v-if="canExportTrack" size="small" icon="el-icon-download" :disabled="loading" @click="exportTrack">导出</el-button>
        <el-button size="small" icon="el-icon-back" @click="backToList">返回</el-button>
      </div>
    </header>
    <waybill-map-dialog
      v-if="loaded && !(shareMode && shareError)"
      ref="mapViewer"
      :visible="true"
      full-page
      :waybill="waybill"
      :joint-details="jointDetails"
      :initial-view="$route.query.view || ''"
      :stopovers="stopovers"
      :share-mode="shareMode"
      :share-scope="shareScope"
      :embed-mode="embedMode"
      :initial-monitor-data="monitorData"
      :initial-alarms="alarms"
      :initial-fences="fences"
      :initial-route-plan="routePlan"
      :share-refresh-handler="shareMode ? loadSharePage : null"
      @close="backToList"
    />
  </div>
</template>

<script>
import { getWaybillDetail, getWaybillShareDetail, isRequestSuccess } from './lingdongApi'
import { hasPermission } from '@/utils/auth'
import WaybillMapDialog from './WaybillMapDialog.vue'

export default {
  name: 'WaybillMapPage',
  components: { WaybillMapDialog },
  props: {
    embedMode: { type: Boolean, default: false },
    embedWaybillNo: { type: String, default: '' }
  },
  data() {
    return {
      loading: false,
      loaded: false,
      waybill: {},
      jointDetails: [],
      loadSequence: 0,
      shareRefreshSequence: 0,
      stopovers: [],
      monitorData: null,
      alarms: [],
      fences: [],
      routePlan: null,
      shareScope: '',
      shareError: false
    }
  },
  created() {
    this.loadWaybill()
  },
  beforeDestroy() {
    this.loadSequence += 1
    this.shareRefreshSequence += 1
  },
  watch: {
    '$route.fullPath'() {
      // 离开地图页时 fullPath 会先变成列表路由，此时不应再 load / backToList
      if (this.$route.params.transportNo || this.$route.query.transportNo || this.$route.params.shareId) {
        this.loadWaybill()
      }
    },
    embedWaybillNo() {
      if (this.embedMode) this.loadWaybill()
    }
  },
  computed: {
    canExportTrack() { return hasPermission('waybill:export') },
    shareMode() {
      return Boolean(this.$route.params.shareId)
    }
  },
  methods: {
    exportTrack() {
      if (!this.canExportTrack || this.shareMode || this.embedMode) return false
      if (!this.$refs.mapViewer) {
        this.$message.warning('轨迹正在加载，请稍后再导出')
        return false
      }
      return this.$refs.mapViewer.openTrackExport()
    },
    async loadWaybill(options = {}) {
      const transportNo = this.embedMode ? this.embedWaybillNo : (this.$route.params.transportNo || this.$route.query.transportNo)
      const shareId = this.$route.params.shareId
      const transportNo2 = !this.embedMode && !shareId ? this.$route.query.transportNo2 : ''
      const sequence = ++this.loadSequence
      this.shareRefreshSequence++
      if (!transportNo && !shareId) {
        this.backToList()
        return
      }
      const silent = options.silent === true
      if (!silent) {
        this.loading = true
        this.loaded = false
      }
      if (shareId) this.shareError = false
      try {
        const res = shareId
          ? await getWaybillShareDetail(shareId, { limit: 2000 })
          : await getWaybillDetail({ transportNo })
        if (!isRequestSuccess(res)) throw new Error(res.msg || '获取运单详情失败')
        if (sequence !== this.loadSequence) return
        const data = res.data || {}
        this.jointDetails = []
        if (transportNo2) {
          const second = await getWaybillDetail({ transportNo: transportNo2 })
          if (sequence !== this.loadSequence) return
          if (!isRequestSuccess(second)) throw new Error(second.msg || '获取第二条运单详情失败')
          const details = [data, second.data || {}]
          const waybills = details.map(item => item.waybill || item)
          if (waybills.some(item => ![1, 2, 4].includes(Number(item.transportStatus)))) throw new Error('仅运输中、已完成或监控结束的运单支持合并回放')
          const vehicle = item => `${String(item.vehicleNo || item.vno || '').replace(/\s+/g, '').toUpperCase()}|${item.plateColor}`
          if (transportNo === transportNo2 || !waybills[0].vehicleNo || vehicle(waybills[0]) !== vehicle(waybills[1])) throw new Error('请选择同一车辆且车牌颜色相同的两条运单')
          this.jointDetails = details
        }
        this.applyDetailData(data, shareId)
        this.loaded = true
      } catch (e) {
        if (sequence !== this.loadSequence) return
        if (shareId) {
          this.clearDetailData()
          this.shareError = true
          return
        }
        this.$message.error(e.msg || e.message || '获取运单详情失败')
        this.backToList()
      } finally {
        if (!silent && sequence === this.loadSequence) this.loading = false
      }
    },
    clearDetailData() {
      this.loaded = false
      this.waybill = {}
      this.jointDetails = []
      this.stopovers = []
      this.monitorData = null
      this.alarms = []
      this.fences = []
      this.routePlan = null
      this.shareScope = ''
    },
    applyDetailData(data, shareId) {
      this.shareScope = shareId ? String(data.scope || 'CURRENT_POSITION') : ''
      this.waybill = data.waybill || data
      this.stopovers = data.stopovers || []
      this.monitorData = shareId ? (data.monitorData || null) : (data.monitor || null)
      this.alarms = data.alarms || []
      this.fences = data.fences || []
      this.routePlan = this.waybill.routeId ? (data.route || null) : null
    },
    async loadSharePage(params = {}) {
      const shareId = this.$route.params.shareId
      if (!shareId) throw new Error('分享链接无效')
      const sequence = ++this.shareRefreshSequence
      const pageSequence = this.loadSequence
      const current = () => sequence === this.shareRefreshSequence && pageSequence === this.loadSequence && shareId === this.$route.params.shareId
      let res
      try { res = await getWaybillShareDetail(shareId, params) } catch (error) { if (!current()) return null; throw error }
      if (!current()) return null
      if (!isRequestSuccess(res)) throw new Error(res.msg || '获取分享轨迹失败')
      const data = res.data || {}
      this.shareScope = String(data.scope || this.shareScope || 'CURRENT_POSITION')
      if (params.afterTime === undefined && params.afterBackfillId === undefined) {
        this.waybill = data.waybill || this.waybill
        this.stopovers = data.stopovers || []
        this.alarms = data.alarms || []
        this.fences = data.fences || []
        this.routePlan = this.waybill.routeId ? (data.route || null) : null
      }
      return data.monitorData || null
    },
    backToList() {
      if (this.embedMode) {
        this.$emit('close')
        return
      }
      if (this.shareMode) {
        return
      }
      if (this.$route.name === 'LingDongInTransitName') return
      const returnQuery = this.$route.query.returnQuery
      this.$router.push({ name: 'LingDongInTransitName', query: returnQuery ? { listState: returnQuery } : {} }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.waybill-map-page { display: flex; flex-direction: column; height: calc(100vh - var(--layout-header-total, 60px)); min-height: 0; max-height: calc(100vh - var(--layout-header-total, 60px)); overflow: hidden; background: #fff; }
.waybill-map-page > .waybill-map-route { min-height: 0; height: auto; flex: 1; }
.waybill-map-page__actions { display: flex; flex: 0 0 54px; align-items: center; justify-content: space-between; gap: 12px; padding: 0 18px; border-bottom: 1px solid #ebeef5; }
.waybill-map-page__actions > div { display: flex; align-items: center; gap: 12px; min-width: 0; }
.waybill-map-page__actions strong { color: #303133; white-space: nowrap; }
.waybill-map-page__actions span { overflow: hidden; color: #909399; font-size: 13px; text-overflow: ellipsis; white-space: nowrap; }
.waybill-map-page__actions ~ .waybill-map-route ::v-deep .function-buttons__export { display: none; }
.waybill-map-page--share { height: 100vh; max-height: 100vh; }
.waybill-map-page--embed { height: 100%; max-height: 100%; }
.waybill-share-error { height: 100%; display: flex; align-items: center; justify-content: center; padding: 24px; box-sizing: border-box; background: #f5f7fa; color: #303133; }
.waybill-share-error__card { width: min(460px, 100%); padding: 48px 36px; box-sizing: border-box; border: 1px solid #ebeef5; border-radius: 12px; background: #fff; text-align: center; box-shadow: 0 8px 28px rgba(31, 45, 61, .08); }
.waybill-share-error__icon { display: block; margin-bottom: 18px; color: #e6a23c; font-size: 48px; }
.waybill-share-error h1 { margin: 0 0 14px; font-size: 24px; font-weight: 600; }
.waybill-share-error p { margin: 0; color: #606266; font-size: 15px; line-height: 1.8; }
@supports (height: 100dvh) {
  .waybill-map-page--share { height: 100dvh; max-height: 100dvh; }
}
</style>
